import React, { useState } from 'react'

// Three customer-facing tiers. Each maps to the underlying monthly/annual
// plan_key depending on the billing-interval toggle. Starter is free.
// `features` render as ✓ rows; `offFeatures` render as muted — rows.
const TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    who: 'Trying it out',
    keys: { month: 'starter', year: 'starter' },
    features: ['1 admin', '1 walkthrough · 1 path', '50 audience members'],
    offFeatures: ['No custom domain', '“Powered by” badge shown']
  },
  {
    id: 'pro',
    name: 'Pro',
    who: 'Growing teams',
    keys: { month: 'pro_monthly', year: 'pro_annual' },
    features: [
      'Unlimited walkthroughs & paths',
      '1 custom domain',
      '1,000 audience members',
      'Reminders & nudges'
    ],
    offFeatures: ['“Powered by” badge shown']
  },
  {
    id: 'premium',
    name: 'Premium',
    who: 'Scaling onboarding',
    recommended: true,
    minimumNote: 'Billed at a 5-admin minimum — you’ll be charged for 5 seats even with fewer, and can add seats anytime.',
    keys: { month: 'premium_monthly', year: 'premium_annual' },
    features: [
      'Everything in Pro',
      '10 custom domains',
      '10,000 audience members',
      'White-label — remove the “Powered by” badge',
      'Priority support'
    ],
    offFeatures: []
  }
]

// "Usage this cycle" cards. Each pairs a live usage count (billing.usage) with
// the current plan's limit (billing.limits). A null/absent limit means the plan
// grants that resource without a cap, so the card renders an "unlimited" state.
const USAGE_METRICS = [
  { key: 'admin_seats', limitKey: 'admins', label: 'Admin seats', unlimitedNote: 'Billed per seat' },
  { key: 'walkthroughs', limitKey: 'walkthroughs', label: 'Walkthroughs', unlimitedNote: 'Unlimited' },
  { key: 'custom_domains', limitKey: 'custom_domains', label: 'Custom domains', unlimitedNote: 'Unlimited' },
  { key: 'audience', limitKey: 'audience', label: 'Audience members', unlimitedNote: 'Unlimited' }
]

// Used to choose Upgrade vs Downgrade wording relative to the current plan.
const PLAN_RANK = {
  starter: 0,
  pro_monthly: 1,
  pro_annual: 1,
  premium_monthly: 2,
  premium_annual: 2,
  early_access_annual: 3
}

const getCsrfToken = () => document.querySelector("meta[name='csrf-token']")?.getAttribute('content')

// Split a pricing display like "$50 per admin / month" into a large amount and
// a small caption so the plan cards can render the mockup's price treatment.
const splitPrice = (display) => {
  if (!display) return { big: '—', small: '' }
  const match = display.match(/^(\$[\d.,]+)\s*(.*)$/)
  if (match) return { big: match[1], small: match[2] }
  return { big: display, small: '' }
}

const BillingPage = ({ billing, plans, checkout_path, portal_path }) => {
  const [submittingPlan, setSubmittingPlan] = useState(null)
  const [submittingPortal, setSubmittingPortal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [billingInterval, setBillingInterval] = useState(billing.interval === 'year' ? 'year' : 'month')

  const paidPlanLocked = billing.paid
  const planByKey = (key) => plans.find(plan => plan.plan_key === key)
  const currentRank = PLAN_RANK[billing.plan_key] ?? 0
  const onPremium = currentRank >= 2

  const premiumKey = billingInterval === 'year' ? 'premium_annual' : 'premium_monthly'
  const premiumPlan = planByKey(premiumKey)

  const submitBillingAction = async ({ path, payload }) => {
    const response = await fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken()
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Unable to start billing flow.')
    }

    if (data.url) {
      window.location.assign(data.url)
    }
  }

  const openCheckout = async (planKey) => {
    setErrorMessage('')
    setSubmittingPlan(planKey)

    try {
      await submitBillingAction({ path: checkout_path, payload: { plan_key: planKey } })
    } catch (error) {
      setErrorMessage(error.message)
      setSubmittingPlan(null)
    }
  }

  const openPortal = async () => {
    setErrorMessage('')
    setSubmittingPortal(true)

    try {
      await submitBillingAction({ path: portal_path, payload: {} })
    } catch (error) {
      setErrorMessage(error.message)
      setSubmittingPortal(false)
    }
  }

  const subText = (tier) => {
    if (tier.id === 'starter') return 'Free forever'
    const cadence = billingInterval === 'year' ? 'Billed annually' : 'Billed monthly'
    return tier.minimumNote ? `${cadence} · 5-seat minimum` : cadence
  }

  // Current-plan meta line, e.g. "$50 per admin / month · 3 admin seats · renews Aug 12, 2026".
  const currentMeta = () => {
    const parts = [billing.pricing?.display].filter(Boolean)
    const seats = billing.seat_count
    parts.push(`${seats} admin seat${seats === 1 ? '' : 's'}`)
    if (billing.cancel_at_period_end && billing.current_period_end) {
      parts.push(`cancels ${new Date(billing.current_period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`)
    } else if (billing.current_period_end) {
      parts.push(`renews ${new Date(billing.current_period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`)
    }
    return parts.join(' · ')
  }

  const renderCurrentPlanActions = () => {
    const actions = []

    if (billing.portal_available) {
      actions.push(
        <button key='portal' className='btn btn-white btn-sm' disabled={submittingPortal} onClick={openPortal} type='button'>
          {submittingPortal ? 'Opening…' : 'Manage billing in Stripe ↗'}
        </button>
      )
    }

    if (!onPremium) {
      if (paidPlanLocked) {
        // Plan changes for an active paid subscription go through the portal.
        actions.push(
          <button key='upgrade' className='btn btn-primary btn-sm' disabled={submittingPortal || !billing.portal_available} onClick={openPortal} type='button'>
            {submittingPortal ? 'Opening…' : 'Upgrade to Premium'}
          </button>
        )
      } else if (premiumPlan && premiumPlan.stripe_price_id) {
        actions.push(
          <button key='upgrade' className='btn btn-primary btn-sm' disabled={submittingPlan === premiumKey} onClick={() => openCheckout(premiumKey)} type='button'>
            {submittingPlan === premiumKey ? 'Opening…' : 'Upgrade to Premium'}
          </button>
        )
      } else {
        actions.push(
          <button key='upgrade' className='btn btn-primary btn-sm' disabled type='button'>
            Premium coming soon
          </button>
        )
      }
    }

    return actions
  }

  const renderUsageCard = (metric) => {
    const usage = billing.usage || {}
    const limits = billing.limits || {}
    const value = Number(usage[metric.key] || 0)
    const rawLimit = limits[metric.limitKey]
    const hasLimit = rawLimit !== null && rawLimit !== undefined

    if (!hasLimit) {
      return (
        <div key={metric.key} className='u'>
          <div className='l'>{metric.label}</div>
          <div className='v'>{value.toLocaleString()}</div>
          <div className='unl'>{metric.unlimitedNote}</div>
        </div>
      )
    }

    const limit = Number(rawLimit)
    const pct = limit === 0 ? 100 : Math.min(100, Math.round((value / limit) * 100))
    const atLimit = value >= limit
    const warn = atLimit || pct >= 80

    let note
    if (limit === 0) {
      note = 'Not included — upgrade to unlock'
    } else if (atLimit) {
      note = 'At your plan limit — upgrade to raise it'
    } else if (warn) {
      note = `${pct}% used — upgrade to raise it`
    } else {
      note = `${pct}% of your plan limit`
    }

    return (
      <div key={metric.key} className={`u ${warn ? 'warn' : ''}`}>
        <div className='l'>{metric.label}</div>
        <div className='v'>{value.toLocaleString()} <small>/ {limit.toLocaleString()}</small></div>
        <div className='bar'><i style={{ width: `${pct}%` }} /></div>
        <div className='note'>{note}</div>
      </div>
    )
  }

  const renderPlanCta = (tier, plan, planKey, isCurrentPlan) => {
    if (isCurrentPlan) {
      return <div className='curbtn'>Current plan</div>
    }

    const btnStyle = tier.recommended ? 'btn-primary' : 'btn-white'
    const targetRank = PLAN_RANK[planKey] ?? 0
    const label = targetRank > currentRank ? `Upgrade to ${tier.name}` : 'Downgrade'

    // Paid plan not seeded in Stripe for this environment yet.
    if (tier.id !== 'starter' && (!plan || !plan.stripe_price_id)) {
      return <button className='btn btn-white cta' disabled type='button'>Coming soon</button>
    }

    // Active paid subscription: all plan changes (up, down, cancel) go through
    // the Stripe Billing Portal.
    if (paidPlanLocked) {
      return (
        <button className={`btn ${btnStyle} cta`} disabled={submittingPortal || !billing.portal_available} onClick={openPortal} type='button'>
          {submittingPortal ? 'Opening…' : label}
        </button>
      )
    }

    // Free company upgrading into a paid plan → direct to Stripe Checkout.
    return (
      <button className={`btn ${btnStyle} cta`} disabled={submittingPlan === planKey} onClick={() => openCheckout(planKey)} type='button'>
        {submittingPlan === planKey ? 'Opening…' : label}
      </button>
    )
  }

  const renderPlanCard = (tier) => {
    const planKey = tier.keys[billingInterval]
    const plan = planByKey(planKey)
    const isCurrentPlan = billing.plan_key === planKey
    const price = splitPrice(plan ? plan.pricing?.display : null)

    return (
      <div key={tier.id} className={`plan ${isCurrentPlan ? 'cur-plan' : ''} ${tier.recommended ? 'rec' : ''}`}>
        {tier.recommended && <span className='rtag'>Recommended for scaling</span>}
        <div className='pn'>{tier.name}</div>
        <div className='who'>{tier.who}</div>
        <div className='price'>{price.big}<small> {price.small}</small></div>
        <div className='sub'>{subText(tier)}</div>
        <ul>
          {tier.features.map(item => (
            <li key={item}><span className='ck'>✓</span>{item}</li>
          ))}
          {tier.offFeatures.map(item => (
            <li key={item} className='off'><span className='ck'>—</span>{item}</li>
          ))}
        </ul>
        {tier.recommended && tier.minimumNote && plan && <div className='minnote'>{tier.minimumNote}</div>}
        <div className='grow' />
        {renderPlanCta(tier, plan, planKey, isCurrentPlan)}
      </div>
    )
  }

  const renderPaymentCard = () => {
    const pm = billing.payment_method

    return (
      <div className='bd'>
        <div className='card pay-method'>
          <div className='row'>
            <span className='pm'>{pm ? (pm.brand || 'Card').toUpperCase() : 'CARD'}</span>
            <div className='pm-copy'>
              <div className='nm'>{pm ? `${pm.brand} ending ${pm.last4}` : 'Payment method'}</div>
              <div className='m'>
                {pm
                  ? `Expires ${pm.exp_month}/${pm.exp_year}`
                  : (billing.paid ? 'Managed securely in Stripe' : 'Added at checkout when you upgrade')}
              </div>
            </div>
            <div className='grow' />
            <button className='btn btn-white btn-sm' disabled={submittingPortal || !billing.portal_available} onClick={openPortal} type='button'>
              {submittingPortal ? 'Opening…' : 'Update'}
            </button>
          </div>
        </div>
        <div className='card invoices'>
          <div className='inv'>
            <span className='nm'>Invoices &amp; receipts</span>
            {billing.portal_available
              ? <button className='linkbtn' disabled={submittingPortal} onClick={openPortal} type='button'>Open in Stripe ↗</button>
              : <span className='m'>Available after your first payment</span>}
          </div>
          <div className='inv'>
            <span className='m'>Download receipts, view invoice history, and update your billing email in the Stripe Billing Portal.</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='billing-section'>
      <div className='sechead'>Current plan</div>
      <div className='card cur'>
        <div><span className='badge'>Current</span></div>
        <div className='cur-copy'>
          <div className='nm'>{billing.display_name}</div>
          <div className='m'>{currentMeta()}</div>
        </div>
        <div className='grow' />
        <div className='cur-actions'>{renderCurrentPlanActions()}</div>
      </div>

      <div className='sechead'>Usage this cycle</div>
      <div className='usage'>
        {USAGE_METRICS.map(renderUsageCard)}
      </div>

      <div className='sechead'>Change plan</div>
      <div className='togrow'>
        <div className='togrow-label'>
          {paidPlanLocked
            ? 'Manage your active subscription in the Stripe Billing Portal.'
            : 'Compare plans and switch anytime.'}
        </div>
        <div className='seg' role='group' aria-label='Billing interval'>
          <button
            type='button'
            className={`s ${billingInterval === 'month' ? 'on' : ''}`}
            aria-pressed={billingInterval === 'month'}
            onClick={() => setBillingInterval('month')}
          >
            Monthly
          </button>
          <button
            type='button'
            className={`s ${billingInterval === 'year' ? 'on' : ''}`}
            aria-pressed={billingInterval === 'year'}
            onClick={() => setBillingInterval('year')}
          >
            Yearly <span className='save'>save ~15%</span>
          </button>
        </div>
      </div>

      {errorMessage && <div className='billing-error'>{errorMessage}</div>}

      <div className='plans'>
        {TIERS.map(renderPlanCard)}
      </div>

      <div className='sechead'>Payment &amp; invoices</div>
      {renderPaymentCard()}
    </div>
  )
}

export default BillingPage
